import React, { createContext, useContext, useReducer, useEffect, ReactNode, useRef } from 'react';
import { User, LoginCredentials, RegisterData } from '../types';
import { authService } from '../services';

// State interface
interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  initializing: boolean;
}

// Action types
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: User }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_USER'; payload: User | null };

// Initial state
const initialState: AuthState = {
  user: null,
  isLoading: false,
  isAuthenticated: false,
  error: null,
  initializing: true,
};

// Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        initializing: false,
        error: null,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isLoading: false,
        isAuthenticated: true,
        initializing: false,
        error: null,
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        isLoading: false,
        isAuthenticated: false,
        initializing: false,
        error: action.payload,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        isLoading: false,
        isAuthenticated: false,
        initializing: false,
        error: null,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
        initializing: false,
      };
    default:
      return state;
  }
};

// Context interface
interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  loginWithGoogle: (idToken: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  refreshUser: () => Promise<void>;
  updateUserRole?: (role: string) => Promise<void>;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
interface AuthProviderProps {
  children: ReactNode;
}

// Global state for circuit breaker and request locking
interface GlobalAuthState {
  isValidationLocked: boolean;
  lastValidationTime: number;
  consecutiveErrors: number;
  circuitBreakerOpen: boolean;
  circuitBreakerOpenTime: number;
}

const globalAuthState: GlobalAuthState = {
  isValidationLocked: false,
  lastValidationTime: 0,
  consecutiveErrors: 0,
  circuitBreakerOpen: false,
  circuitBreakerOpenTime: 0,
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Constants for rate limiting and circuit breaker
  const VALIDATION_DEBOUNCE_MS = 10000; // 10 seconds between validations
  const CIRCUIT_BREAKER_THRESHOLD = 5; // Open circuit after 5 consecutive errors
  const CIRCUIT_BREAKER_TIMEOUT = 60000; // Close circuit after 1 minute

  // Token validation with circuit breaker and global locking
  const validateTokenWithServer = async (): Promise<void> => {
    const now = Date.now();

    // Check if circuit breaker is open
    if (globalAuthState.circuitBreakerOpen) {
      if (now - globalAuthState.circuitBreakerOpenTime < CIRCUIT_BREAKER_TIMEOUT) {
        console.log('Circuit breaker is open, skipping token validation');
        return;
      } else {
        // Close circuit breaker
        globalAuthState.circuitBreakerOpen = false;
        globalAuthState.consecutiveErrors = 0;
        console.log('Circuit breaker closed, resuming token validation');
      }
    }

    // If we have a recent validation, skip
    if (now - globalAuthState.lastValidationTime < VALIDATION_DEBOUNCE_MS) {
      console.log('Skipping token validation - recently validated');
      return;
    }

    // If validation is locked, skip
    if (globalAuthState.isValidationLocked) {
      console.log('Token validation is locked, skipping');
      return;
    }

    // Lock validation globally
    globalAuthState.isValidationLocked = true;

    try {
      console.log('Starting token validation...');
      await authService.getCurrentUser();

      // Success: update state
      globalAuthState.lastValidationTime = now;
      globalAuthState.consecutiveErrors = 0;
      console.log('Token validation successful');

    } catch (error: any) {
      // Handle errors and circuit breaker logic
      globalAuthState.consecutiveErrors++;
      console.error(`Token validation failed (${globalAuthState.consecutiveErrors}/${CIRCUIT_BREAKER_THRESHOLD}):`, error.message);

      // Open circuit breaker if threshold reached
      if (globalAuthState.consecutiveErrors >= CIRCUIT_BREAKER_THRESHOLD) {
        globalAuthState.circuitBreakerOpen = true;
        globalAuthState.circuitBreakerOpenTime = now;
        console.warn('Circuit breaker opened due to consecutive validation failures');
      }

      throw error;
    } finally {
      // Always unlock validation
      globalAuthState.isValidationLocked = false;
    }
  };

  // Check for existing auth on mount
  useEffect(() => {
    const initAuth = async () => {
      const storedUser = authService.getStoredUser();
      const isAuth = authService.isAuthenticated();

      if (isAuth && storedUser) {
        dispatch({ type: 'SET_USER', payload: storedUser });

        // Validate token with server - but don't auto-logout on network errors
        try {
          await validateTokenWithServer();
        } catch (error: any) {
          // Only logout if we get a 401 response (token actually invalid)
          // For network errors or server issues, keep the user logged in
          if (error.response?.status === 401) {
            dispatch({ type: 'LOGOUT' });
            authService.logout();
          } else {
            // For other errors (network, server down, rate limit), keep user logged in
            console.warn('Token validation failed, but keeping user logged in:', error.message);
          }
        }
      } else {
        // No stored auth, set initializing to false
        dispatch({ type: 'LOGOUT' });
      }
    };

    initAuth();
  }, []);

  // Login function
  const login = async (credentials: LoginCredentials): Promise<void> => {
    try {
      dispatch({ type: 'AUTH_START' });

      const response = await authService.login(credentials);

      if (response.success && response.data) {
        dispatch({ type: 'AUTH_SUCCESS', payload: response.data.user });
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Login failed';
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
      throw error;
    }
  };

  // Register function
  const register = async (userData: RegisterData): Promise<void> => {
    try {
      dispatch({ type: 'AUTH_START' });

      const response = await authService.register(userData);

      if (response.success && response.data) {
        dispatch({ type: 'AUTH_SUCCESS', payload: response.data.user });
      } else {
        throw new Error(response.message || 'Registration failed');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Registration failed';
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
      throw error;
    }
  };

  // Login with Google
  const loginWithGoogle = async (idToken: string): Promise<void> => {
    try {
      dispatch({ type: 'AUTH_START' });

      const response = await authService.loginWithGoogle(idToken);

      if (response.success && response.data) {
        dispatch({ type: 'AUTH_SUCCESS', payload: response.data.user });
      } else {
        throw new Error(response.message || 'Google login failed');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Google login failed';
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
      throw error;
    }
  };

  // Logout function
  const logout = async (): Promise<void> => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      dispatch({ type: 'LOGOUT' });
    }
  };

  // Clear error function
  const clearError = (): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // Refresh user data
  const refreshUser = async (): Promise<void> => {
    try {
      await validateTokenWithServer();

      // After successful validation, update user data from localStorage
      const storedUser = authService.getStoredUser();
      if (storedUser) {
        dispatch({ type: 'SET_USER', payload: storedUser });
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        // Token expired, logout
        await logout();
      }
      throw error;
    }
  };

  // Update user role
  const updateUserRole = async (role: string): Promise<void> => {
    try {
      // Call API to update user role
      const response = await authService.updateUserRole(role);

      if (response.success && response.data) {
        dispatch({ type: 'AUTH_SUCCESS', payload: response.data.user });
      } else {
        throw new Error(response.message || 'Failed to update role');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update role';
      throw new Error(errorMessage);
    }
  };

  const value: AuthContextType = {
    ...state,
    login,
    register,
    loginWithGoogle,
    logout,
    clearError,
    refreshUser,
    updateUserRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
